<template>
  <div class="form-container">
    <h1 class="page-title">建物マスタ情報編集</h1>
    <form @submit.prevent="handleSubmit" @keydown.enter.prevent>
      <div class="mb-3">
        <label for="id" class="form-label">ID：</label>
        {{ building.id}}
      </div>

      <div class="mb-3">
        <label for="customer" class="form-label">顧客 <span class="badge text-bg-danger">必須</span></label>
        <CustomerSelectBox
          v-model="building.m_customer_id"
          :options="customers"
          :required="true"
        />
      </div>

      <div class="mb-3">
        <label for="buildingType" class="form-label">区分 <span class="badge text-bg-danger">必須</span></label>
        <BuildingTypeSelectBox
          v-model="building.m_building_type_id"
          :options="buildingTypes"
          :required="true"
        />
      </div>

      <div class="mb-3">
        <label for="buildingName" class="form-label">建物名 <span class="badge text-bg-danger">必須</span></label>
        <input v-model="building.building_nm" type="text" placeholder="建物名" class="form-control" id="buildingName" required>
        <div v-if="errorMessages.building_nm" class="alert alert-danger mt-2">{{ errorMessages.building_nm[0] }}</div>
      </div>

      <PostalCodeInput
        v-model="building.postal_code"
        @address="({ region, locality, street }) => {
          building.prefecture = region;
          building.city = locality;
          building.other_address = street;
        }"
      />

      <div class="mb-3">
        <label for="prefecture" class="form-label">都道府県</label>
        <input v-model="building.prefecture" type="text" placeholder="都道府県" class="form-control" id="prefecture">
        <div v-if="errorMessages.prefecture" class="alert alert-danger mt-2">{{ errorMessages.prefecture[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="city" class="form-label">市区町村</label>
        <input v-model="building.city" type="text" placeholder="市区町村" class="form-control" id="city">
        <div v-if="errorMessages.city" class="alert alert-danger mt-2">{{ errorMessages.city[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="otherAddress" class="form-label">その他の住所</label>
        <input v-model="building.other_address" type="text" placeholder="その他の住所" class="form-control" id="otherAddress">
        <div v-if="errorMessages.other_address" class="alert alert-danger mt-2">{{ errorMessages.other_address[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="telNum" class="form-label">電話番号</label>
        <input v-model="building.tel_num" type="text" placeholder="電話番号" class="form-control" id="telNum">
        <div v-if="errorMessages.tel_num" class="alert alert-danger mt-2">{{ errorMessages.tel_num[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="contractDt" class="form-label">契約日</label>
        <input
          v-model="building.contract_dt"
          type="date"
          id="contractDt"
          class="form-control"
          max="2099-12-31"
          placeholder="契約日"
        >
      </div>

      <div class="mb-3">
        <label for="inChargeNm" class="form-label">担当者</label>
        <input v-model="building.in_charge_nm" type="text" placeholder="担当者" class="form-control" id="inChargeNm">
        <div v-if="errorMessages.in_charge_nm" class="alert alert-danger mt-2">{{ errorMessages.in_charge_nm[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="inChargePhoneNum" class="form-label">携帯番号</label>
        <input v-model="building.in_charge_phone_num" type="text" placeholder="携帯番号" class="form-control" id="inChargePhoneNum">
        <div v-if="errorMessages.in_charge_phone_num" class="alert alert-danger mt-2">{{ errorMessages.in_charge_phone_num[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="inChargeEmail" class="form-label">担当者メールアドレス</label>
        <input v-model="building.in_charge_email" type="text" placeholder="担当者メールアドレス" class="form-control" id="inChargeEmail">
        <div v-if="errorMessages.in_charge_email" class="alert alert-danger mt-2">{{ errorMessages.in_charge_email[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="fixed_fee_billing" class="ECM_CheckboxInput">
          <input
            v-model="building.fixed_fee_billing"
            :true-value="true"
            :false-value="false"
            id="fixed_fee_billing"
            type="checkbox"
            class="ECM_CheckboxInput-Input"
          >
          <span class="ECM_CheckboxInput-DummyInput"></span>
          <h4 class="ms-3">定額請求対象</h4>
        </label>
      </div>

      <div class="mb-3">
        <label for="user" class="form-label">社内担当者</label>
        <UserSelectBox
          v-model="building.internal_person_id"
          :options="users"
          :required="true"
        />
      </div>

      <div class="mb-3">
        <label for="contractEndDt" class="form-label">契約終了日</label>
        <input
          v-model="building.contract_end_dt"
          type="date"
          id="contractEndDt"
          class="form-control"
          max="2099-12-31"
          placeholder="契約終了日"
        >
      </div>

      <div class="btn-area">
        <a class="btn btn-secondary mx-1" @click="$router.back()">戻る</a>
        <button type="submit" class="btn btn-success mx-1" :disabled="isSubmitting">更新</button>
      </div>
    </form>

    <div v-if="showCustomerChangeModal" class="customer-change-modal" role="dialog" aria-modal="true" aria-labelledby="customerChangeTitle">
      <div class="customer-change-modal__backdrop" @click="!isSubmitting && closeCustomerChangeModal()"></div>
      <div class="customer-change-modal__dialog">
        <h2 id="customerChangeTitle" class="h5 mb-3">顧客変更の適用開始年月</h2>
        <p class="mb-2">
          「{{ originalCustomerName }}」から「{{ selectedCustomerName }}」へ変更します。
        </p>
        <p class="small text-muted mb-3">
          変更後顧客の請求締日（{{ selectedCustomerDeadlineLabel }}締め）を基準に初期値を設定しています。
          指定年月より前に作成済みの予定表・請求書は変更されません。
        </p>
        <div class="mb-3">
          <label for="customerUpdateFrom" class="form-label">年間作業予定の適用開始年月</label>
          <input
            id="customerUpdateFrom"
            v-model="customerUpdateFrom"
            type="month"
            class="form-control"
            required
          >
          <div v-if="customerUpdateFromError" class="text-danger small mt-1">{{ customerUpdateFromError }}</div>
        </div>
        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-secondary" :disabled="isSubmitting" @click="closeCustomerChangeModal">キャンセル</button>
          <button type="button" class="btn btn-success" :disabled="isSubmitting" @click="submitWithCustomerChange">
            この年月以降を変更して更新
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  getSelectBoxCustomers,
  getBuildingTypes,
  getSelectBoxUsers,
  getBuilding,
  updateBuilding
} from '@/api/axios';
import CustomerSelectBox from '@/components/searchSelectBoxes/CustomerSelectBox.vue';
import BuildingTypeSelectBox from '@/components/searchSelectBoxes/BuildingTypeSelectBox.vue';
import UserSelectBox from '@/components/searchSelectBoxes/UserSelectBox.vue';
import PostalCodeInput from '@/components/shared/PostalCodeInput.vue';
import {
  billingDeadlineLabel,
  defaultCustomerChangeEffectiveMonth
} from '@/utils/customerChangeEffectiveMonth';

export default {
  components: {
    CustomerSelectBox,
    BuildingTypeSelectBox,
    UserSelectBox,
    PostalCodeInput
  },
  setup() {
    const title = ref('建物情報編集');

    watchEffect(() => {
      document.title = title.value;
    });

    const customers = ref([]);
    const buildingTypes = ref([]);
    const users = ref([]);
    const building = ref({
      m_customer_id: '',
      m_building_type_id: '',
      building_nm: '',
      postal_code: '',
      prefecture: '',
      city: '',
      other_address: '',
      tel_num: '',
      contract_dt: '',
      in_charge_nm: '',
      in_charge_phone_num: '',
      in_charge_email: '',
      fixed_fee_billing: '',
      contract_end_dt: '',
      internal_person_id: ''
      // 他のフィールド
    });
    const originalCustomerId = ref(null);
    const showCustomerChangeModal = ref(false);
    const customerUpdateFrom = ref('');
    const customerUpdateFromError = ref('');
    const isSubmitting = ref(false);

    // エラーメッセージ用のリアクティブプロパティ
    const errorMessages = ref({});

    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const buildingId = route.params.id; // URLから建物情報IDを取得

    const loadBuildingData = async () => {
      try {
        const [buildingResponse, customerResponse, buildingTypeResponse, userResponse] = await Promise.all([
          getBuilding(buildingId),
          getSelectBoxCustomers(),
          getBuildingTypes(),
          getSelectBoxUsers()
        ]);

        building.value = buildingResponse.data;
        originalCustomerId.value = buildingResponse.data.m_customer_id;
        customers.value = customerResponse.data;
        buildingTypes.value = buildingTypeResponse.data.building_types;
        users.value = userResponse.data;
      } catch (error) {
        console.error('建物情報・顧客情報・建物種別情報・ユーザ情報の取得に失敗しました:', error);
      }
    };

    const selectedCustomer = computed(() =>
      customers.value.find(customer => String(customer.id) === String(building.value.m_customer_id))
    );
    const originalCustomer = computed(() =>
      customers.value.find(customer => String(customer.id) === String(originalCustomerId.value))
    );
    const selectedCustomerName = computed(() => selectedCustomer.value?.customer_nm || '変更後の顧客');
    const originalCustomerName = computed(() => originalCustomer.value?.customer_nm || '変更前の顧客');
    const selectedCustomerDeadlineLabel = computed(() =>
      billingDeadlineLabel(selectedCustomer.value?.billing_deadline)
    );

    const customerChanged = () =>
      String(building.value.m_customer_id) !== String(originalCustomerId.value);

    const openCustomerChangeModal = () => {
      customerUpdateFrom.value = defaultCustomerChangeEffectiveMonth(
        selectedCustomer.value?.billing_deadline
      );
      customerUpdateFromError.value = '';
      showCustomerChangeModal.value = true;
    };

    const closeCustomerChangeModal = () => {
      showCustomerChangeModal.value = false;
      customerUpdateFromError.value = '';
    };

    const persistBuilding = async (effectiveMonth = null) => {
      isSubmitting.value = true;
      try {
        await updateBuilding(buildingId, building.value, effectiveMonth);
        store.dispatch('setFlashMessage', {message: '建物情報更新完了！', type: 'success'});
        router.push({
          path: `/buildings/${buildingId}`,
          query: route.query
        });
      } catch (error) {
        if (error.response && error.response.data) {
          errorMessages.value = error.response.data || {};
          customerUpdateFromError.value = error.response.data.customer_update_from?.[0] || '';
        }
        store.dispatch('setFlashMessage', {message: '建物情報更新失敗！', type: 'danger'});
        console.error('更新エラー:', error);
      } finally {
        isSubmitting.value = false;
      }
    };

    const submitWithCustomerChange = async () => {
      if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(customerUpdateFrom.value)) {
        customerUpdateFromError.value = '適用開始年月を入力してください。';
        return;
      }

      await persistBuilding(customerUpdateFrom.value);
    };

    const handleSubmit = async () => {
      if (customerChanged()) {
        openCustomerChangeModal();
        return;
      }

      await persistBuilding();
    };

    onMounted(loadBuildingData);

    return {
      title,
      customers,
      buildingTypes,
      users,
      building,
      handleSubmit,
      errorMessages,
      showCustomerChangeModal,
      customerUpdateFrom,
      customerUpdateFromError,
      isSubmitting,
      selectedCustomerName,
      originalCustomerName,
      selectedCustomerDeadlineLabel,
      closeCustomerChangeModal,
      submitWithCustomerChange
    };
  }
};
</script>

<style scoped>
.customer-change-modal {
  align-items: center;
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1rem;
  position: fixed;
  z-index: 1055;
}

.customer-change-modal__backdrop {
  background: rgba(0, 0, 0, 0.5);
  inset: 0;
  position: absolute;
}

.customer-change-modal__dialog {
  background: #fff;
  border-radius: 0.5rem;
  max-width: 540px;
  padding: 1.5rem;
  position: relative;
  width: 100%;
  z-index: 1;
}
</style>
